import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery } from "@apollo/client";

import { Title, Button } from "rbx";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader";
import FollowupAddEditForm from "../FollowupAddEditForm/FollowupAddEditForm";
import { useCurrentUser } from "../../../../hooks";
import { CREATE_ONE_USER_FOLLOWUP, UPDATE_ONE_USER_FOLLOWUP, SEND_FOLLOWUP_ASSIGNMENT_NOTIFICATION } from "../../graphql";
import { GET_EMPLOYEES_WITH_GROUP } from "../../../../graphql";

const INITIAL_STATE = {
  title: "",
  description: "",
  assignedToEmployeeNumber: "",
};

class CustomProjectError extends Error {}

const FollowupFormModal = ({ onComplete, userData, type, editFollowup }) => {
  const [followup, setFollowup] = useState(editFollowup?.title ? {
    title: editFollowup?.title, 
    description: editFollowup?.description,
    assignedToEmployeeNumber: editFollowup?.assignedToUser?.employeeNumber || "",
    followupDate: editFollowup?.followupDate || ""
  } : { ...INITIAL_STATE });

  const [createOneUserFollowup, { loading: loadingCreateUserFollowup }] =
    useMutation(CREATE_ONE_USER_FOLLOWUP);

  // Get employees for assignment lookup
  const { data: employeesData } = useQuery(GET_EMPLOYEES_WITH_GROUP, {
    variables: {},
  });

  const handleInputChange = (name, value) => {
    setFollowup((prev) => ({ ...prev, [name]: value }));
  };

  const user = useCurrentUser();
  const employees = employeesData?.getEmployeesWithGroup || [];

  // Find assigned user ID from employee number
  const findAssignedUserId = (employeeNumber) => {
    if (!employeeNumber) return null;
    const assignedEmployee = employees.find(emp => emp.employeeNumber === employeeNumber);
    return assignedEmployee?.id || null;
  };

  const CREATE_NEW_FOLLOWUP = {
    variables: {
      data: {
        title: followup.title,
        description: followup.description,
        status: "Active",
        currentProgress: 0,
        createdBy: `${user?.firstName} ${user?.lastName}`,
        createdByID: user?.id,
        createDate: new Date().toISOString(),
        followupDate: followup.followupDate ? new Date(followup.followupDate).toISOString() : null,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.employeeNumber,
        updatedByFullName: `${user?.firstName} ${user?.lastName}`,
        user: { connect : { id: userData?.id } },
        ...(followup.assignedToEmployeeNumber && {
          assignedToUser: { connect: { id: findAssignedUserId(followup.assignedToEmployeeNumber) } }
        }),
      },
    }
  };

  const EDIT_NEW_FOLLOWUP = {
    variables: {
      where: {
        id: editFollowup?.id,
      },
      data: {
        title: { set: followup.title,},
        description: { set: followup.description,},
        followupDate: { set: followup.followupDate ? new Date(followup.followupDate).toISOString() : null,},
        updatedAt: { set: new Date().toISOString(),},
        updatedBy: { set: user?.employeeNumber,},
        updatedByFullName: { set: `${user?.firstName} ${user?.lastName}`,},
      },
    }
  };

  const [sendFollowupAssignmentNotification] = useMutation(SEND_FOLLOWUP_ASSIGNMENT_NOTIFICATION);

  const sendAssignmentNotification = async (followupId, assignedEmployeeNumber) => {
    if (!assignedEmployeeNumber || !followupId) return;
    
    try {
      await sendFollowupAssignmentNotification({
        variables: { followupId }
      });
      console.log("Assignment notification sent successfully");
    } catch (error) {
      console.error("Error sending assignment notification:", error);
      // Don't fail the whole operation if notification fails
    }
  };

  const handleCreate = async (e) => {
    try {
      e.preventDefault();
      const result = await createOneUserFollowup(CREATE_NEW_FOLLOWUP);
      
      // Send notification if assigned and followup was created successfully
      if (followup.assignedToEmployeeNumber && result?.data?.createOneUserFollowup?.id) {
        await sendAssignmentNotification(result.data.createOneUserFollowup.id, followup.assignedToEmployeeNumber);
      }
      
      toast.success(`Follow-up created successfully.`);
      onComplete();
    } catch (error) {
      const message =
        error instanceof CustomProjectError
          ? error.message
          : `Error creating follow-up. Please contact support.`;
      toast.error(message);
    }
  };

    // eslint-disable-next-line no-unused-vars
  const [updateOneUserFollowup, { loading: loadingFollowup }] = useMutation(
    UPDATE_ONE_USER_FOLLOWUP
  );

  const handleEdit = async (e) => {
    try {
      e.preventDefault();
      await updateOneUserFollowup(EDIT_NEW_FOLLOWUP);

      toast.success("Successfully updated follow-up");
      onComplete();
    } catch (err) {
      toast.error("Error saving follow-up, please contact support.");
    }
  };

  const handleSubmit = async (e) => {
    if(type === "Create") {
      await handleCreate(e);
    } else {
      await handleEdit(e);
    }
  }


  const isLoading = loadingCreateUserFollowup;

  if (isLoading) return <Loader />;

  const isDisabled =
    !followup.title ||
    !followup.description

  return (
    <form id="followup-form" onSubmit={handleSubmit}>
      <header className="modal-head">
        <div className="modal-head-start">
          <Title size={5}>{type === "Edit" ? "Edit Follow-up":"Create Follow-up"}</Title>
        </div>
        <div className="modal-head-end">
          <Button.Group hasAddons>
            <Button
              size="small"
              type="button"
              onClick={() => onComplete(false)}
            >
              <span>Cancel</span>
            </Button>
            <Button
              color="primary"
              disabled={isDisabled}
              form="followup-form"
              size="small"
              state={isLoading ? "loading" : ""}
              type="submit"
            >
              <span>Save</span>
            </Button>
          </Button.Group>
        </div>
      </header>
      <hr />
      <FollowupAddEditForm followup={followup} onChange={handleInputChange} type={type} />
    </form>
  );
};

FollowupFormModal.propTypes = {
  onComplete: PropTypes.func.isRequired,
  userData: PropTypes.object.isRequired,
  type: PropTypes.string,
  editFollowup: PropTypes.object
};

FollowupFormModal.defaultProps = {
  editFollowup: {},
  type: "Create"
};

export default FollowupFormModal; 